// ErrorBoundary.js
import React from 'react';
import { COLORS, RADIUS, SHADOW } from './theme';

// The one class component in this codebase — React only supports error boundaries via
// componentDidCatch/getDerivedStateFromError, no hook equivalent exists. Wraps the whole app in
// index.js so an uncaught render-time exception anywhere in the tree lands here instead of
// crashing to a blank white screen with no recovery. No retry-in-place: the error that got here
// already means some component's state is in a shape its own render couldn't handle, and reloading
// is the one recovery that is actually reliable.
//
// WHY IT NOW SHOWS THE ERROR. This screen said only "Something went wrong", and the detail went to
// a browser console the owner of this app has no reason to ever open. Twice now a crash has been
// reported as a screenshot of this card, which carries exactly as much information as the sentence
// on it: the Workflow Canvas, which turned out to have been down for five weeks, and Report Builder.
// Both cost a hunt that the error's own first line would have ended.
//
// Shown behind a toggle, so the default remains a calm sentence for a technician mid-job, and the
// detail is one tap away for somebody reporting it. Copy puts the same text on the clipboard,
// because a screenshot of a stack trace is not searchable.
//
// Deliberately NOT sent anywhere. Piping a client-side crash into the errorLogs collection would
// need its own callable endpoint and a rules change to a collection every other write path treats
// as Functions-only trusted input — still a separate decision, and showing the text needs neither.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, details: '', showDetails: false, copied: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Uncaught render error:', error, errorInfo);
    // Built defensively: this runs while the app is ALREADY broken, and a throw in here replaces
    // the recovery card with the blank screen it exists to prevent.
    let details = '';
    try {
      const stack = String(error?.stack || '').split('\n').slice(0, 6).join('\n');
      const where = String(errorInfo?.componentStack || '').trim().split('\n').slice(0, 8).join('\n');
      details = [
        `${error?.name || 'Error'}: ${error?.message || String(error)}`,
        where ? `\nWhere:\n${where}` : '',
        stack ? `\nStack:\n${stack}` : '',
        `\nPage: ${window.location?.pathname || ''}`
      ].filter(Boolean).join('\n');
    } catch (readError) {
      details = 'The error could not be read.';
    }
    this.setState({ details });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const { details, showDetails, copied } = this.state;
    const linkButton = {
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      fontSize: '12.5px', color: COLORS.primary, textDecoration: 'underline'
    };
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: COLORS.bgPage, padding: '24px', boxSizing: 'border-box'
      }}>
        <div style={{
          maxWidth: '520px', width: '100%', backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`,
          borderRadius: RADIUS.lg, boxShadow: SHADOW.raised, padding: '32px', textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 12px', fontSize: '18px', color: COLORS.text }}>Something went wrong</h2>
          <p style={{ margin: '0 0 24px', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.5 }}>
            This screen hit an unexpected error and couldn't continue. Nothing you were working on before this should be lost — reloading usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px', fontSize: '14px', fontWeight: '600', color: COLORS.white,
              backgroundColor: COLORS.primary, border: 'none', borderRadius: RADIUS.sm, cursor: 'pointer'
            }}
          >
            Reload
          </button>
          {details && (
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
              <button
                type="button"
                onClick={() => this.setState({ showDetails: !showDetails })}
                style={linkButton}
              >
                {showDetails ? 'Hide technical details' : 'Show technical details'}
              </button>
              {showDetails && (
                <>
                  <pre style={{
                    margin: '12px 0 8px', padding: '12px', textAlign: 'left', whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word', fontSize: '11.5px', lineHeight: 1.45, color: COLORS.text,
                    backgroundColor: COLORS.bgPage, border: `1px solid ${COLORS.border}`,
                    borderRadius: RADIUS.sm, maxHeight: '260px', overflowY: 'auto'
                  }}>{details}</pre>
                  <button
                    type="button"
                    onClick={() => {
                      // Clipboard access can be refused outright (insecure context, denied
                      // permission). Failing here would be a second crash on the crash screen.
                      try {
                        navigator.clipboard.writeText(details)
                          .then(() => this.setState({ copied: true }))
                          .catch(() => this.setState({ copied: false }));
                      } catch (copyError) {
                        this.setState({ copied: false });
                      }
                    }}
                    style={linkButton}
                  >
                    {copied ? 'Copied' : 'Copy details'}
                  </button>
                  <p style={{ margin: '10px 0 0', fontSize: '11.5px', color: COLORS.textFaint, lineHeight: 1.45 }}>
                    Send this to whoever maintains the app — it names the exact screen and line that failed.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
